import { type ElementType, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { FORMULA_HEALTH_LIMITS, mathHealthStrictness } from '../shared/mathHealth';
import { normalizeMathMarkdown } from './math';
import remarkMathHealth from './remarkMathHealth';

type RichTextProps = {
  text: string | null | undefined;
  as?: ElementType;
  className?: string;
  mode?: 'inline' | 'block';
};

type ChildrenProps = { children?: ReactNode };

const safeLink: Components['a'] = ({ children, href }) => (
  <a href={href} target="_blank" rel="nofollow noopener noreferrer">{children}</a>
);

const imageAlt: Components['img'] = ({ alt }) => alt ? <span className="rich-image-alt">{alt}</span> : null;

const inlineComponents: Components = {
  p: ({ children }: ChildrenProps) => <span className="rich-paragraph">{children}</span>,
  h1: ({ children }: ChildrenProps) => <span className="rich-heading">{children}</span>,
  h2: ({ children }: ChildrenProps) => <span className="rich-heading">{children}</span>,
  h3: ({ children }: ChildrenProps) => <span className="rich-heading">{children}</span>,
  h4: ({ children }: ChildrenProps) => <span className="rich-heading">{children}</span>,
  h5: ({ children }: ChildrenProps) => <span className="rich-heading">{children}</span>,
  h6: ({ children }: ChildrenProps) => <span className="rich-heading">{children}</span>,
  blockquote: ({ children }: ChildrenProps) => <span className="rich-quote">{children}</span>,
  ul: ({ children }: ChildrenProps) => <span className="rich-list">{children}</span>,
  ol: ({ children }: ChildrenProps) => <span className="rich-list">{children}</span>,
  li: ({ children }: ChildrenProps) => <span className="rich-list-item">{children}</span>,
  pre: ({ children }: ChildrenProps) => <span className="rich-pre">{children}</span>,
  table: ({ children }: ChildrenProps) => <span className="rich-table">{children}</span>,
  thead: ({ children }: ChildrenProps) => <span>{children}</span>,
  tbody: ({ children }: ChildrenProps) => <span>{children}</span>,
  tr: ({ children }: ChildrenProps) => <span className="rich-table-row">{children}</span>,
  th: ({ children }: ChildrenProps) => <span className="rich-table-cell">{children}</span>,
  td: ({ children }: ChildrenProps) => <span className="rich-table-cell">{children}</span>,
  a: safeLink,
  img: imageAlt,
};

const blockComponents: Components = {
  a: safeLink,
  img: imageAlt,
  table: ({ children }) => <div className="rich-table-scroll"><table>{children}</table></div>,
};

export function RichText({ text, as, className = '', mode = 'inline' }: RichTextProps) {
  const effectiveMode = mode === 'block' || as === 'p' ? 'block' : 'inline';
  const Component = effectiveMode === 'block' ? 'div' : as || 'span';
  const markdown = normalizeMathMarkdown(String(text || ''));
  return (
    <Component data-ui-preserve="true" className={`rich-text rich-text-${effectiveMode} ${className}`.trim()}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: true }], remarkMathHealth]}
        rehypePlugins={[[rehypeKatex, {
          throwOnError: false,
          strict: (errorCode: string) => mathHealthStrictness(errorCode),
          trust: false,
          maxExpand: FORMULA_HEALTH_LIMITS.maxExpand,
          maxSize: FORMULA_HEALTH_LIMITS.maxSize,
          output: 'htmlAndMathml',
          errorColor: '#475569',
        }]]}
        components={effectiveMode === 'block' ? blockComponents : inlineComponents}
        skipHtml
      >
        {markdown}
      </ReactMarkdown>
    </Component>
  );
}
